/** Courses: my courses and the catalogue, the course page, checkout and the receipt. */
import { api, openPrintable } from '../../core/api.js';
import { config } from '../../core/config.js';
import { INDIAN_STATES } from '../../core/constants.js';
import { register } from '../../core/events.js';
import { html } from '../../core/html.js';
import { fmtDate, inr } from '../../core/format.js';
import { navigate } from '../../core/router.js';
import { session } from '../../core/session.js';
import { currentView, update } from '../../app/shell.js';
import { backLink, banner, bar, empty, ic, kv, seg, statusTag, tag } from '../../ui/components.js';
import { selectField } from '../../ui/forms.js';
import { toast } from '../../ui/toast.js';
import { payOrder } from '../billing/pay.js';
import { resumeMissing, resumeNotice } from '../resume/card.js';
import { CERTIFICATE_NOTE, courseCards, courseDetail } from '../courses/views.js';
import { demoCard, loadDemos } from './demo.js';

/* ---------- my courses + catalogue ---------- */

const learn = {
  path: '/student/learn',
  roles: ['student'],
  title: 'Courses',
  ui: () => ({ q: '', category: 'All' }),
  async load(ctx) {
    const [mine, catalogue, demos] = await Promise.all([
      api.get('/me/enrollments', { signal: ctx.signal }),
      api.get('/courses', { signal: ctx.signal, query: { limit: 50 } }),
      loadDemos(ctx.signal),
    ]);
    return { enrollments: mine.enrollments, courses: catalogue.courses, demos };
  },
  view(d, ui) {
    const enrolledIds = new Set(d.enrollments.map((e) => e.course.id));
    const categories = ['All', ...new Set(d.courses.map((c) => c.category))];
    const q = ui.q.trim().toLowerCase();
    const list = d.courses.filter((c) => !enrolledIds.has(c.id)
      && (ui.category === 'All' || c.category === ui.category)
      && (!q || `${c.title} ${(c.skills || []).join(' ')} ${c.trainer?.name || ''}`.toLowerCase().includes(q)));
    return html`${demoCard(d.demos)}<h3 class="mt">My courses</h3>
      ${d.enrollments.length
        ? html`<div class="grid g3">${d.enrollments.map((e) => html`<div class="card"><div class="row between"><span class="tag n">${e.course.category}</span>${e.has_access ? '' : tag('Access ended', 'wn')}</div>
            <h3 class="mt-s">${e.course.title}</h3>${bar(e.progress?.percent ?? 0, '', e.course.title)}<p class="xs mu mt-s">${e.progress?.percent ?? 0}% complete${e.current_period_end ? `. Paid until ${fmtDate(e.current_period_end)}` : ''}</p>
            <div class="row"><a class="btn sm" href="/student/classes">Classes</a><a class="btn ghost sm" href="/student/course/${e.course.slug}">Details</a></div></div>`)}</div>`
        : empty('book', 'No courses yet', 'Pick a course below to join a live batch.')}
      <div class="mt">${banner(CERTIFICATE_NOTE, '', 'award')}</div>
      <div class="page-h mt"><h3 style="margin:0">Explore courses</h3><div class="row">
        <input style="width:220px" placeholder="Search courses or skills" value="${ui.q}" aria-label="Search courses" data-input="learn.search">${seg(categories, ui.category, 'learn.category', 'Category')}</div></div>
      ${list.length ? courseCards(list, { base: '/student/course' }) : empty('search', 'No courses match', 'Try a different search or category.', html`<button class="btn ghost" data-act="learn.clear">Clear filters</button>`)}`;
  },
};

register({
  'learn.search': (el) => update((ui) => { ui.q = el.value; }),
  'learn.category': (el) => update((ui) => { ui.category = el.dataset.value; }),
  'learn.clear': () => update((ui) => { ui.q = ''; ui.category = 'All'; }),
});

/* ---------- course page ---------- */

const course = {
  path: '/student/course/:slug',
  roles: ['student'],
  nav: 'learn',
  title: (d) => d?.course?.title || 'Course',
  async load(ctx) {
    const [detail, mine, trials, noResume] = await Promise.all([
      api.get(`/courses/${encodeURIComponent(ctx.params.slug)}`, { signal: ctx.signal }),
      api.get('/me/enrollments', { signal: ctx.signal }),
      api.get('/student/trials', { signal: ctx.signal }),
      resumeMissing(ctx.signal),
    ]);
    return { noResume, ...detail, enrollment: mine.enrollments.find((e) => e.course.id === detail.course.id) || null, myTrial: trials.trials.find((t) => t.course_id === detail.course.id) || null };
  },
  view(d) {
    const e = d.enrollment;
    const canEnrol = d.batches.length > 0 || Boolean(e?.batch_id);
    const trial = d.trial;
    const trialBlock = e || !trial?.enabled ? ''
      : d.myTrial ? html`${banner(`Your free trial class is booked${d.myTrial.starts_at ? ` for ${fmtDate(d.myTrial.starts_at)}` : ''}.`, 'good', 'check')}<a class="btn ghost wide mt-s" href="/student/classes">See my classes</a>`
        : trial.next_at ? html`<button type="button" class="btn ghost wide mt-s" data-act="course.trial" data-id="${d.course.id}">${ic('cal')} Book a free trial class</button><p class="xs mu mt-s">Next trial class: ${fmtDate(trial.next_at)}</p>`
          : html`<p class="note mt-s">${ic('cal')} Free trial class: dates will be announced soon.</p>`;
    const action = e?.has_access
      ? html`${banner('You are enrolled in this batch.', 'good', 'check')}<a class="btn wide mt-s" href="/student/classes">Open my classes</a>`
      : d.noResume && canEnrol ? resumeNotice()
      : html`${canEnrol
        ? (d.course.price_paise === 0
          ? html`<button type="button" class="btn lg wide mt-s" data-act="course.register" data-id="${d.course.id}">Register free</button>`
          : html`<a class="btn lg wide mt-s" href="/student/checkout/${d.course.slug}">${e ? 'Renew and continue' : 'Subscribe and join a batch'}</a>`)
        : html`<button type="button" class="btn lg wide mt-s" disabled>Enrolment opens soon</button><p class="note mt-s">You can pay as soon as the start date is announced.</p>`}
        ${trialBlock}`;
    return html`${backLink('/student/learn', 'Courses')}${courseDetail({ course: d.course, trainer: d.trainer, batches: d.batches, gst: d.gst_percent ?? 0, action, trial })}`;
  },
};

register({
  'course.register': async (el) => {
    try {
      await api.post('/billing/enrol-free', { body: { course_id: Number(el.dataset.id) } });
      toast('You are registered. Your classes are under Live classes.', 'check');
      await navigate('/student/classes');
    } catch (error) {
      toast(error.message, 'alert');
    }
  },
  'course.trial': async (el) => {
    await api.post('/student/trials', { body: { course_id: Number(el.dataset.id) } });
    toast('Trial class booked. See your classes.', 'cal');
    await navigate('/student/classes');
  },
});

/* ---------- checkout ---------- */

async function loadQuote(ctx, courseId, coupon) {
  return api.get('/billing/quote', { signal: ctx.signal, query: { course_id: courseId, coupon } });
}

const checkout = {
  path: '/student/checkout/:slug',
  roles: ['student'],
  nav: 'learn',
  title: 'Checkout',
  ui: () => ({ batchId: null, coupon: '', couponInput: '', state: 'Telangana', couponError: '', error: '', paying: false }),
  async load(ctx) {
    const detail = await api.get(`/courses/${encodeURIComponent(ctx.params.slug)}`, { signal: ctx.signal });
    const [quote, noResume, mine] = await Promise.all([loadQuote(ctx, detail.course.id, ''), resumeMissing(ctx.signal), api.get(`/billing/courses/${detail.course.id}/batches`, { signal: ctx.signal })]);
    return { ...detail, batches: mine.batches, quote, noResume };
  },
  view(d, ui) {
    const q = d.quote;
    const batchId = ui.batchId ?? d.batches[0]?.id ?? null;
    return html`${backLink(`/student/course/${d.course.slug}`, 'Course')}
      <div class="grid g2" style="align-items:start;max-width:980px">
        <div class="card"><h3>Choose your batch</h3>
          ${d.batches.length
            ? d.batches.map((b) => html`<label class="opt ${batchId === b.id ? 'on' : ''}"><input type="radio" name="batch" value="${b.id}" ${batchId === b.id ? 'checked' : ''} data-change="checkout.batch"><div><b>Batch ${b.name}</b><div class="sm mu">${b.schedule}${b.starts_on ? `. Starts ${fmtDate(b.starts_on)}` : ''}</div><div class="xs">${b.seats_left} ${b.seats_left === 1 ? 'seat' : 'seats'} left</div></div></label>`)
            : banner(d.trial?.enabled ? 'Enrolment is not open yet. You can book a free trial class from the course page, and you can pay as soon as the start date is announced.' : 'Enrolment is not open yet. You can pay as soon as the start date is announced.', 'wn', 'info')}
          <div class="mt-s">${selectField('co-state', 'Billing state', INDIAN_STATES.map((s) => [s, s]), { name: 'billing_state', val: ui.state, act: 'checkout.state', help: config.gstRegistered ? 'Decides how GST is shown on your invoice.' : 'Shown on your invoice.' })}</div></div>
        <div class="card"><h3>Order summary</h3><p class="sm"><b>${d.course.title}</b></p>
          <table><tr><td>Monthly subscription</td><td style="text-align:right">${inr(q.base_paise)}</td></tr>
            ${q.discount_paise ? html`<tr><td>Coupon ${q.coupon}</td><td style="text-align:right;color:var(--gr)">−${inr(q.discount_paise)}</td></tr>` : ''}
            ${q.gst_percent ? html`<tr><td>GST ${q.gst_percent}%</td><td style="text-align:right">${inr(q.gst_paise)}</td></tr>` : ''}
            <tr><td><b>Total today</b></td><td style="text-align:right"><b>${inr(q.total_paise)}</b></td></tr></table>
          <div class="row nw mt-s"><input placeholder="Coupon code" aria-label="Coupon code" value="${ui.couponInput}" data-input="checkout.couponInput" data-enter="checkout.applyCoupon" ${ui.coupon ? 'disabled' : ''}>
            ${ui.coupon ? html`<button type="button" class="btn ghost" data-act="checkout.removeCoupon">Remove</button>` : html`<button type="button" class="btn ghost" data-act="checkout.applyCoupon">Apply</button>`}</div>
          ${ui.couponError ? html`<p class="xs" style="color:var(--rd)" role="alert">${ui.couponError}</p>` : ''}
          ${ui.error ? banner(ui.error, 'bad', 'alert') : ''}
          ${d.noResume ? resumeNotice() : ''}
          <button type="button" class="btn lg wide mt" data-act="checkout.pay" ${batchId && !d.noResume ? '' : 'disabled'}>Pay ${inr(q.total_paise)}</button>
          <p class="note mt-s">${ic('lock')} ${config.paymentMode === 'manual' ? 'You pay by UPI or bank transfer and we confirm it. Access starts once we do.' : 'Payments are processed by Razorpay. We never see your card or UPI details.'}</p></div></div>`;
  },
};

register({
  'checkout.batch': (el) => update((ui) => { ui.batchId = Number(el.value); }),
  'checkout.state': (el) => update((ui) => { ui.state = el.value; }),
  'checkout.couponInput': (el) => { currentView().ui.couponInput = el.value; },
  'checkout.applyCoupon': async (el) => {
    const code = (el.closest('.card').querySelector('input[aria-label="Coupon code"]').value || '').trim().toUpperCase();
    if (!code) return;
    const view = currentView();
    try {
      view.data.quote = await loadQuote(view.ctx, view.data.course.id, code);
      update((ui) => { ui.coupon = code; ui.couponInput = code; ui.couponError = ''; });
      toast('Coupon applied', 'check');
    } catch (error) {
      if (error.status === 0) throw error;
      update((ui) => { ui.couponError = error.message; });
    }
  },
  'checkout.removeCoupon': async () => {
    const view = currentView();
    view.data.quote = await loadQuote(view.ctx, view.data.course.id, '');
    update((ui) => { ui.coupon = ''; ui.couponInput = ''; ui.couponError = ''; });
  },
  'checkout.pay': async () => {
    const view = currentView();
    const { ui, data } = view;
    update((u) => { u.error = ''; });
    const { order } = await api.post('/billing/checkout', {
      body: { course_id: data.course.id, batch_id: ui.batchId ?? data.batches[0]?.id, coupon_code: ui.coupon || undefined, billing_state: ui.state },
    });
    const user = session.user;
    const result = await payOrder(order, { description: data.course.title, prefill: { name: user.name, email: user.email, contact: user.phone } });
    if (result.status === 'paid') {
      await navigate(`/student/enrolled/${result.order.id}`, { replace: true });
    } else if (result.status === 'pending') {
      await navigate('/student/billing', { replace: true });
    } else if (result.status === 'failed') {
      update((u) => { u.error = `${result.message} You have not been charged. Please try again.`; });
    } else {
      toast('Payment cancelled. You have not been charged.', 'alert');
    }
  },
});

/* ---------- receipt ---------- */

const enrolled = {
  path: '/student/enrolled/:orderId',
  roles: ['student'],
  nav: 'learn',
  title: 'You are enrolled',
  async load(ctx) {
    const [orders, invoices, mine] = await Promise.all([
      api.get('/billing/orders', { signal: ctx.signal }),
      api.get('/billing/invoices', { signal: ctx.signal }),
      api.get('/me/enrollments', { signal: ctx.signal }),
    ]);
    const order = orders.orders.find((o) => String(o.id) === ctx.params.orderId);
    if (!order) throw Object.assign(new Error('We could not find this order.'), { status: 404 });
    return {
      order,
      invoice: invoices.invoices.find((i) => i.order_id === order.id) || null,
      enrollment: mine.enrollments.find((e) => e.course.id === order.course_id) || null,
    };
  },
  view(d) {
    const { order, invoice, enrollment } = d;
    const paid = order.status === 'paid';
    return html`<div class="card" style="max-width:620px;margin:auto">
      ${paid ? banner(html`<b>Payment successful.</b> You are enrolled.`, 'good', 'check') : banner(html`Your payment is ${statusTag(order.status)}. If money was taken it will be refunded.`, 'wn', 'alert')}
      <h2 class="mt">${enrollment?.course.title || 'Your course'}</h2>
      ${kv([['Receipt', `#${order.id}`], ['Date', fmtDate(order.paid_at || order.created_at)], ['Amount', config.gstRegistered ? `${inr(order.total_paise)} incl. GST` : inr(order.total_paise)], ['Status', statusTag(order.status)], invoice ? ['Invoice', invoice.number] : null])}
      <div class="row mt"><a class="btn" href="/student/classes">Go to live classes</a>
        ${invoice ? html`<button type="button" class="btn ghost" data-act="invoice.print" data-number="${invoice.number}">${ic('dl')} Tax invoice</button>` : ''}</div></div>`;
  },
};

register({
  'invoice.print': (el) => openPrintable(`/billing/invoices/${encodeURI(el.dataset.number)}/print`),
});

export const routes = [learn, course, checkout, enrolled];
